import { sendUpdateUserPasswordReset } from "@/models/socket/user.model";
import { AppContext } from "@/services/AppContext/appContext";
import { Button } from "@/shadcn/components/ui/button";
import { Input } from "@/shadcn/components/ui/input";
import { Label } from "@/shadcn/components/ui/label";
import { genericFailureToast, genericSuccessToast } from "@/utils/utils.util";
import { validatePassword } from "@/utils/validation.util";
import md5 from "md5";
import React, { useContext, useState } from "react";

const PasswordReset: React.FC = () => {
  const [newPassword, setNewPassword] = useState<string>("");
  const [confirmPassword, setConfirmPassword] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const { setUserShouldUpdateAccount } = useContext(AppContext);

  async function handlePasswordReset() {
    if (!newPassword.trim() || !confirmPassword.trim()) {
      setError("Please fill in both password fields.");
      return;
    }

    if (!validatePassword(newPassword)) {
      setError(
        "Password must be at least 8 characters long and include 1 uppercase and 1 special character"
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const hashPassword = md5(newPassword);

    if (!hashPassword) {
      setError("Failed to hash password.");
      setSubmitting(false);
      console.error("Failed to hash password.");
      return;
    }

    const result = await sendUpdateUserPasswordReset(hashPassword);
    if (result.rStatus === "error") {
      setSubmitting(false);
      console.error("Failed to reset password:", result.rData);
      setError(
        result.rData || "Failed to reset password. Please try again later."
      );
      genericFailureToast(
        result.rData || "Failed to reset password. Please try again later."
      );
      return;
    }
    setSubmitting(false);
    setNewPassword("");
    setConfirmPassword("");
    genericSuccessToast("Password reset successfully!");
    setUserShouldUpdateAccount(false);
  }

  return (
    <div className="">
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Label className="block text-base font-semibold">
          New Password
          <Input
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            className="mt-1 block w-full border p-2"
          />
        </Label>

        <Label className="block text-base font-semibold">
          Confirm Password
          <Input
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            className="mt-1 block w-full border p-2"
          />
        </Label>
      </div>
      {error && <p className="mt-2 text-sm text-red-500">{error}</p>}
      <div className="flex justify-end">
        <Button
          onClick={handlePasswordReset}
          disabled={submitting}
          className="mt-4 w-auto"
          size={"sm"}
        >
          {submitting ? "Submitting..." : "Reset Password"}
        </Button>
      </div>
    </div>
  );
};

export default PasswordReset;
